import React, { memo } from 'react'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import CommentIcon from 'transfix-icons/build/CommentIcon'
import EmailIcon from 'transfix-icons/build/EmailIcon'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import PhoneIcon from 'transfix-icons/build/PhoneIcon'
import { makeStyles, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import {
  FP_FAQ_URL,
  FP_STATUS_PAGE_URL,
  FP_SUPPORT_EMAIL,
  FP_SUPPORT_PHONE,
  FP_SUPPORT_PHONE_HOURS,
} from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ModalBase, MODAL_TYPES } from 'transfix-ui'
import { SupportItem } from './SupportItem'

const useSupportModalStyles = makeStyles(theme => {
  // NOTE: this margin offset is to be able to define full width content,
  //  in this case, specifically, the `SupportItem`s.
  const modalBodyMarginOffset = theme.spacing(3)

  return {
    modal: {
      "& .modal-footer [class*='btn']": {
        width: '100%',
      },
    },
    list: {
      marginRight: `-${modalBodyMarginOffset}px`,
      marginLeft: `-${modalBodyMarginOffset}px`,
      width: `calc(100% + 2 * ${modalBodyMarginOffset}px)`,
    },
  }
})

interface ISupportModalProps {
  /**
   * Is the support modal visible to the user?
   */
  isOpen: boolean
  /**
   * Exposes the 'on close' life cycle of the modal (as expected of a controlled component).
   */
  onClose: (action?: string) => void
}

export const SupportModal = memo<ISupportModalProps>(({ isOpen, onClose }) => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const { show } = useChatWidgetContext()
  const classes = useSupportModalStyles()

  const liveChatClickHandler = () => {
    show()
  }
  const emailClickHandler = () => trackEvent('Support', 'CLICKED_SUPPORT_EMAIL')
  const faqClickHandler = () => trackEvent('Support', 'CLICKED_FAQ_LINK')
  const statusPageClickHandler = () => trackEvent('Support', 'CLICKED_STATUS_PAGE_LINK')
  const isLiveChatLinkVisible = !!__INTERCOM_APP_ID__

  return (
    <ModalBase
      modalType={MODAL_TYPES.confirmation}
      onClose={onClose}
      open={isOpen}
      className={classes.modal}
      title='Support'
      titleSize={4}
      size={3}
      actions={[
        {
          label: 'Close',
          type: 'confirm',
        },
      ]}>
      <Typography variant={isMobile ? 'caption' : 'body2'}>
        We&apos;re here to help! Below are some ways to reach out to us.
      </Typography>

      <div className={classes.list}>
        {isLiveChatLinkVisible && (
          <SupportItem
            icon={CommentIcon}
            title='Live Chat'
            description={FP_SUPPORT_PHONE_HOURS}
            onClick={liveChatClickHandler}
          />
        )}
        <SupportItem
          href={`tel:${FP_SUPPORT_PHONE}`}
          icon={PhoneIcon}
          title='646-844-2200'
          description={FP_SUPPORT_PHONE_HOURS}
        />
        <SupportItem
          href={`mailto:${FP_SUPPORT_EMAIL}`}
          icon={EmailIcon}
          title={FP_SUPPORT_EMAIL}
          description="We'll respond as soon as possible"
          onClick={emailClickHandler}
        />
        <SupportItem
          href={FP_FAQ_URL}
          target='blank'
          icon={InfoIcon}
          title='Visit our FAQs'
          description='Tutorials &amp; answers to common FAQs'
          onClick={faqClickHandler}
        />
        <SupportItem
          href={FP_STATUS_PAGE_URL}
          target='blank'
          icon={CheckOutlineIcon}
          title='Check Status Page'
          description='Get updates on our system status'
          onClick={statusPageClickHandler}
        />
      </div>
    </ModalBase>
  )
})
